import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Grid,
  Container,
  Paper,
  MenuItem,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Link,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import Navbar from "../../../../globals/components/Navbar";
import { Bar } from "react-chartjs-2";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../../../globals/utils/api";
import { useSelector, useDispatch } from "react-redux";
import { setLawfirmId } from "../../../../store/slices/lawFirmSlice";
import { toast } from "react-toastify";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

// Register Chart.js components explicitly
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

function LawFirmDetail() {
  const navigate = useNavigate();
  const { id } = useParams(); // Assuming lawfirmId is passed in the route
  const dispatch = useDispatch();
  const [lawFirmData, setLawFirmData] = useState({});
  const [graphData, setGraphData] = useState({
    labels: [],
    datasets: [
      {
        label: "Bond History",
        backgroundColor: "#1976d2", // Blue theme for chart
        data: [],
      },
    ],
  });
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Manages saving loader
  const [teamMembers, setTeamMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const { role } = useSelector((state) => state.auth);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Search states
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");

  // Pagination state for locations
  const [locationsPage, setLocationsPage] = useState(0);
  const [locationsRowsPerPage, setLocationsRowsPerPage] = useState(5);

  // Pagination state for team members
  const [teamMembersPage, setTeamMembersPage] = useState(0);
  const [teamMembersRowsPerPage, setTeamMembersRowsPerPage] = useState(5);

  // --- Location Delete State ---
  const [deleteLocationDialogOpen, setDeleteLocationDialogOpen] =
    useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [deleteLocationLoading, setDeleteLocationLoading] = useState(false);

  // --- Law Firm User Delete State ---
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // --- Internal Users for Managed by ---
  const [internalUsers, setInternalUsers] = useState([]);

  const fetchInternalUsers = async () => {
    try {
      const internalUsers = await apiClient("/internal-user", "GET");
      setInternalUsers(internalUsers.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLawFirmDetails = async () => {
    if (!id) {
      console.warn("id is undefined or null");
      return;
    }
    try {
      localStorage.setItem("lawfirm_id", id);
      setLoading(true);
      const lawFirmData = await apiClient(`/lawfirm/${id}`, "GET");
      setLawFirmData(lawFirmData);

      // Fetch locations
      const locationData = await apiClient(
        `/get-locations-by-lawfirm/${id}`,
        "GET"
      );
      setLocations(locationData);

      // Fetch team members
      const teamData = await apiClient(`/lawfirm-user-location/${id}`, "GET");
      setTeamMembers(teamData);
    } catch (error) {
      console.error("Error fetching law firm details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBondHistoryData = async () => {
    try {
      const response = await apiClient(
        `/count-by-year/?law_firm_id=${id}`,
        "GET"
      );
      const data = response;
      const labels = data.map((item) => item.year || "Unknown");
      const counts = data.map((item) => item.count);
      setGraphData({
        labels,
        datasets: [
          {
            label: "Bond History",
            backgroundColor: "#1976d2",
            data: counts,
          },
        ],
      });
    } catch (error) {
      console.error("Error fetching graph data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch law firm details, locations, and team members on mount
  useEffect(() => {
    fetchLawFirmDetails();
    fetchBondHistoryData();
    fetchInternalUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => setIsEditing(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await apiClient(`/lawfirm/${id}`, "PATCH", lawFirmData);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating law firm details:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = (index, field, setFunction, data) => {
    const updatedData = [...data];
    updatedData[index][field] = !updatedData[index][field];
    setFunction(updatedData);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset search queries when switching tabs
    setLocationSearchQuery("");
    setUserSearchQuery("");
    // Reset pagination when switching tabs
    setLocationsPage(0);
    setTeamMembersPage(0);
  };

  const handleViewLawFirmBonds = () => {
    if (!id) return;
    const numericId = Number(id);
    dispatch(setLawfirmId(Number.isNaN(numericId) ? id : numericId));
    navigate("/vermost_approve_bonds_lawfirm");
  };

  // Search handlers
  const handleLocationSearch = (event) => {
    setLocationSearchQuery(event.target.value);
    setLocationsPage(0); // Reset to first page when searching
  };

  const handleUserSearch = (event) => {
    setUserSearchQuery(event.target.value);
    setTeamMembersPage(0); // Reset to first page when searching
  };

  const clearLocationSearch = () => {
    setLocationSearchQuery("");
    setLocationsPage(0);
  };

  const clearUserSearch = () => {
    setUserSearchQuery("");
    setTeamMembersPage(0);
  };

  // Pagination handlers for locations
  const handleLocationsPageChange = (event, newPage) => {
    setLocationsPage(newPage);
  };

  const handleLocationsRowsPerPageChange = (event) => {
    setLocationsRowsPerPage(parseInt(event.target.value, 10));
    setLocationsPage(0);
  };

  // Pagination handlers for team members
  const handleTeamMembersPageChange = (event, newPage) => {
    setTeamMembersPage(newPage);
  };

  const handleTeamMembersRowsPerPageChange = (event) => {
    setTeamMembersRowsPerPage(parseInt(event.target.value, 10));
    setTeamMembersPage(0);
  };

  // Filter functions
  const filterLocations = (locations, searchQuery) => {
    if (!searchQuery.trim()) return locations;
    
    const query = searchQuery.toLowerCase().trim();
    return locations.filter(location => 
      location.name && location.name.toLowerCase().includes(query)
    );
  };

  const filterTeamMembers = (members, searchQuery) => {
    if (!searchQuery.trim()) return members;
    
    const query = searchQuery.toLowerCase().trim();
    return members.filter(member => 
      member.lawfirm_user_name && member.lawfirm_user_name.toLowerCase().includes(query)
    );
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getBondRetrievePath = (bondTypeRaw, bondId) => {
    if (!bondTypeRaw || !bondId) return null;
    const key = String(bondTypeRaw).toLowerCase().replace(/[^a-z]/g, "");

    switch (key) {
      case "probate":
        return `/vermost-probate-bond-update/${bondId}`;
      case "guardianship":
        return `/vermost-guardianship-bond-update/${bondId}`;
      case "trustee":
        return `/vermost-trustee-bond-update/${bondId}`;
      case "va":
        return `/vermost-va-bond-update/${bondId}`;
      case "blanket":
        return `/vermost-blanket-bond-update/${bondId}`;
      default:
        return null;
    }
  };

  const renderReassignComment = (comment) => {
    const text = comment == null ? "" : String(comment);
    if (!text.trim()) return "-";

    // Matches "(Probate-6644)" / "(VA-123)" etc. and turns those into links.
    const regex = /\(([A-Za-z ]+)-(\d+)\)/g;
    let match;
    let lastIndex = 0;
    const nodes = [];

    while ((match = regex.exec(text)) !== null) {
      const [full, bondType, bondId] = match;
      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index));
      }

      const href = getBondRetrievePath(bondType, bondId);
      if (href) {
        nodes.push(
          <Link
            key={`${match.index}-${bondType}-${bondId}`}
            href={href}
            target="_blank"
            rel="noreferrer"
            underline="always"
            sx={{ fontWeight: 600 }}
          >
            {full}
          </Link>
        );
      } else {
        nodes.push(full);
      }

      lastIndex = match.index + full.length;
    }

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }

    return nodes.length ? <>{nodes}</> : text;
  };

  const formatDateForInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  };

  // Get filtered and paginated data
  const filteredLocations = filterLocations(locations, locationSearchQuery);
  const filteredTeamMembers = filterTeamMembers(teamMembers, userSearchQuery);

  const paginatedLocations = filteredLocations.slice(
    locationsPage * locationsRowsPerPage,
    locationsPage * locationsRowsPerPage + locationsRowsPerPage
  );

  const paginatedTeamMembers = filteredTeamMembers.slice(
    teamMembersPage * teamMembersRowsPerPage,
    teamMembersPage * teamMembersRowsPerPage + teamMembersRowsPerPage
  );

  // --- Location Delete Logic ---
  const handleDeleteLocationClick = (location) => {
    setSelectedLocation(location);
    setDeleteLocationDialogOpen(true);
  };

  const handleConfirmDeleteLocation = async () => {
    if (!selectedLocation) return;
    setDeleteLocationLoading(true);
    try {
      await apiClient(
        `/lawfirm-location/${selectedLocation.law_firm_location_id}`,
        "DELETE"
      );
      setLocations(
        locations.filter(
          (loc) =>
            loc.law_firm_location_id !== selectedLocation.law_firm_location_id
        )
      );
    } catch (error) {
      console.error("Error deleting location:", error);
    } finally {
      setDeleteLocationLoading(false);
      setDeleteLocationDialogOpen(false);
      setSelectedLocation(null);
    }
  };

  // --- Law Firm User Delete Logic ---
  const handleDeleteUserClick = (user) => {
    setSelectedUser(user);
    setDeleteUserDialogOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!selectedUser) return;
    setDeleteUserLoading(true);
    try {
      await apiClient(
        `/lawfirm-user/${selectedUser.lawfirm_user_id}/${selectedUser.location_id}`,
        "DELETE"
      );
      setTeamMembers(
        teamMembers.filter(
          (user) => user.lawfirm_user_id !== selectedUser.lawfirm_user_id
        )
      );
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setDeleteUserLoading(false);
      setDeleteUserDialogOpen(false);
      setSelectedUser(null);
    }
  };

  // --- Re-assign Policies Dialog State ---
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignFromMember, setReassignFromMember] = useState(null);
  const [replacementUsers, setReplacementUsers] = useState([]);
  const [selectedReplacementUser, setSelectedReplacementUser] = useState(null);
  const [selectedBondOptions, setSelectedBondOptions] = useState([]);
  const [availableBonds, setAvailableBonds] = useState([]);
  const [bondsLoading, setBondsLoading] = useState(false);
  const [bondsError, setBondsError] = useState("");
  const [reassignSubmitting, setReassignSubmitting] = useState(false);

  // --- Law Firm Re-assignment Logs Dialog State ---
  const [reassignLogsDialogOpen, setReassignLogsDialogOpen] = useState(false);
  const [reassignLogs, setReassignLogs] = useState([]);
  const [reassignLogsLoading, setReassignLogsLoading] = useState(false);
  const [reassignLogsError, setReassignLogsError] = useState("");

  const openReassignDialog = async (member) => {
    setReassignFromMember(member);
    const samePositionUsers =
      teamMembers?.filter(
        (u) =>
          u.Position === member.Position &&
          u.lawfirm_user_id !== member.lawfirm_user_id
      ) || [];
    setReplacementUsers(samePositionUsers);
    setSelectedReplacementUser(null);
    setSelectedBondOptions([]);
    setAvailableBonds([]);
    setBondsError("");
    setReassignDialogOpen(true);

    if (!member?.lawfirm_user_id) return;
    try {
      setBondsLoading(true);
      const data = await apiClient(
        `/list-lawfirm-user-bonds/${member.lawfirm_user_id}/`,
        "GET"
      );
      setAvailableBonds(Array.isArray(data) ? data : []);
      if (!data || !data.length) {
        setBondsError("No bonds found for this user.");
      }
    } catch (error) {
      console.error("Failed to load bonds for law firm user", error);
      setAvailableBonds([]);
      setBondsError("Failed to load bonds for this user.");
    } finally {
      setBondsLoading(false);
    }
  };

  const handleSubmitReassign = async () => {
    if (!selectedReplacementUser || selectedBondOptions.length === 0) return;
    const targetUserId = selectedReplacementUser.value;
    const sourceUserId = reassignFromMember?.lawfirm_user_id;
    const allSelected = selectedBondOptions.some((opt) => opt.value === "ALL");
    const bondIds = allSelected
      ? availableBonds.map((b) => b.bond_id)
      : selectedBondOptions.map((opt) => opt.value);

    if (!bondIds.length) {
      setBondsError("No bonds selected to re-assign.");
      return;
    }

    if (!sourceUserId) {
      setBondsError(
        "Unable to determine the current law firm user for reassignment."
      );
      return;
    }

    try {
      setReassignSubmitting(true);
      const response = await apiClient(
        `/update-lawfirm-user-bonds/${sourceUserId}/`,
        "POST",
        {
          bond_ids: bondIds,
          old_lawfirm_user_id: sourceUserId,
          new_lawfirm_user_id: targetUserId,
        }
      );

      const updatedCount = Number(response?.updated_count || 0);
      toast.success(
        updatedCount
          ? `${updatedCount} bond${updatedCount > 1 ? "s" : ""} re-assigned successfully.`
          : "Re-assignment completed."
      );
      setReassignDialogOpen(false);
      setReassignFromMember(null);
      setAvailableBonds([]);
      setSelectedBondOptions([]);
      setSelectedReplacementUser(null);
      setBondsError("");
    } catch (error) {
      console.error("Failed to re-assign bonds", error);
      const errorMessage =
        error?.error ||
        error?.detail ||
        error?.message ||
        "Failed to re-assign bonds. Please check the selection and try again.";
      setBondsError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setReassignSubmitting(false);
    }
  };

  const handleOpenReassignLogs = async () => {
    if (!id) return;
    setReassignLogsDialogOpen(true);
    setReassignLogs([]);
    setReassignLogsError("");
    setReassignLogsLoading(true);

    try {
      const response = await apiClient(
        `/lawfirm-reassignment-logs/${id}/`,
        "GET"
      );

      const logs = Array.isArray(response?.logs) ? response.logs : [];
      setReassignLogs(logs);

      if (!logs.length) {
        setReassignLogsError(
          "No reassignment activity found for this law firm."
        );
      }
    } catch (error) {
      console.error("Failed to load reassignment logs", error);
      const message =
        error?.detail ||
        error?.message ||
        "Failed to load reassignment logs. Please try again.";
      setReassignLogsError(message);
    } finally {
      setReassignLogsLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const options = {
    scales: {
      y: {
        ticks: {
          beginAtZero: true,
          precision: 0,
        },
      },
    },
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Grid container spacing={3}>
          {/* Top Left: Law Firm Details */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3}
              sx={{
                borderRadius: "16px",
                height: "fit-content"
              }}
            >
              <CardHeader
                title="Law Firm Details"
                sx={{
                  backgroundColor: "#1976d2", // Consistent blue theme
                  color: "white",
                  "& .MuiCardHeader-title": {
                    fontSize: "1.5rem",
                    fontWeight: "600"
                  }
                }}
                action={
                  <Button
                    variant="contained"
                    onClick={handleViewLawFirmBonds}
                    startIcon={<VisibilityIcon />}
                    sx={{
                      fontWeight: 600,
                      borderRadius: "20px",
                      backgroundColor: "white",
                      color: "#1976d2",
                      boxShadow: "none",
                      textTransform: "none",
                      '&:hover': {
                        backgroundColor: "#bbdefb",
                        boxShadow: "none"
                      }
                    }}
                  >
                    View Law Firm Bonds
                  </Button>
                }
              />
              <CardContent sx={{ padding: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Law Firm Name"
                      name="name"
                      fullWidth
                      variant="outlined"
                      value={lawFirmData.name || ""}
                      onChange={(e) =>
                        setLawFirmData({ ...lawFirmData, name: e.target.value })
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  {/* Managed by */}
                  {(role === "super_user" || role === "internal_user") && (
                    <Grid item xs={12}>
                      <TextField
                        select
                        label="Managed By"
                        name="managed_by"
                        fullWidth
                        variant="outlined"
                        value={lawFirmData.managed_by || ""}
                        onChange={(e) =>
                          setLawFirmData({
                            ...lawFirmData,
                            managed_by: e.target.value,
                          })
                        }
                        disabled={!isEditing}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {internalUsers.map((user) => (
                          <MenuItem
                            key={user.internal_user_id}
                            value={user.internal_user_id}
                          >
                            {user.full_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Website"
                      name="website"
                      fullWidth
                      variant="outlined"
                      value={lawFirmData.website || ""}
                      onChange={(e) =>
                        setLawFirmData({
                          ...lawFirmData,
                          website: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Date of First Bond"
                      name="first_bond_date"
                      type="date"
                      fullWidth
                      variant="outlined"
                      value={formatDateForInput(lawFirmData.first_bond_date)}
                      onChange={(e) =>
                        setLawFirmData({
                          ...lawFirmData,
                          first_bond_date: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Accounting Contact Full Name"
                      name="accountingContactName"
                      fullWidth
                      variant="outlined"
                      value={lawFirmData.accounting_contact_full_name || ""}
                      onChange={(e) =>
                        setLawFirmData({
                          ...lawFirmData,
                          accounting_contact_full_name: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Accounting Contact Email"
                      name="accountingContactEmail"
                      fullWidth
                      variant="outlined"
                      value={lawFirmData.accounting_contact_email || ""}
                      onChange={(e) =>
                        setLawFirmData({
                          ...lawFirmData,
                          accounting_contact_email: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Accounting Contact Phone"
                      name="accountingContactPhone"
                      fullWidth
                      variant="outlined"
                      value={lawFirmData.accounting_contact_phone || ""}
                      onChange={(e) =>
                        setLawFirmData({
                          ...lawFirmData,
                          accounting_contact_phone: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                  {isEditing ? (
                    <>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleCancelClick}
                        sx={{ minWidth: 100 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={isSaving}
                        sx={{ minWidth: 100 }}
                      >
                        {isSaving ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Save"
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleEditClick}
                      sx={{ minWidth: 100 }}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Right: Bond History */}
          {(role === "super_user" || role === "internal_user") && (
            <Grid item xs={12} md={6}>
              <Card 
                elevation={3}
                sx={{ borderRadius: "16px" }}
              >
                <CardHeader
                  title="Bond History"
                  sx={{
                    backgroundColor: "#1565c0", // Consistent blue theme (darker blue)
                    color: "white",
                    "& .MuiCardHeader-title": {
                      fontSize: "1.5rem",
                      fontWeight: "600"
                    }
                  }}
                />
                <CardContent sx={{ padding: 3, marginBottom: 2 }}>
                  <Bar data={graphData} options={options} />
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Full Width: Tabbed Section for Locations and Users */}
          <Grid item xs={12}>
            <Card 
              elevation={3}
              sx={{ borderRadius: "16px" }}
            >
              {/* Tab Headers */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange}
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontSize: "1.1rem",
                      fontWeight: "600",
                      minHeight: "64px",
                      color: "#1976d2", // Blue text
                      "&.Mui-selected": {
                        backgroundColor: "white",
                        borderBottom: "3px solid #1976d2",
                        color: "#1976d2"
                      },
                      "&:hover": {
                        backgroundColor: "#bbdefb"
                      }
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#1976d2",
                      height: "3px"
                    }
                  }}
                >
                  <Tab 
                    icon={<LocationOnIcon />} 
                    iconPosition="start"
                    label={`Locations (${filteredLocations.length}${locationSearchQuery ? ` of ${locations.length}` : ''})`}
                  />
                  <Tab 
                    icon={<PeopleIcon />} 
                    iconPosition="start"
                    label={`Law Firm Users (${filteredTeamMembers.length}${userSearchQuery ? ` of ${teamMembers.length}` : ''})`}
                  />
                </Tabs>
              </Box>

              {/* Tab Content */}
              <CardContent sx={{ padding: 0 }}>
                {/* Locations Tab */}
                {tabValue === 0 && (
                  <Box>
                    <Box sx={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      alignItems: "center",
                      p: 3,
                      backgroundColor: "#e3f2fd" // Light blue background
                    }}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: "600", 
                        color: "#1976d2", // Blue text
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                      }}>
                        <LocationOnIcon />
                        Manage Locations
                      </Typography>
                      <Button
                        onClick={() => navigate(`/add_location/${id}`)}
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        sx={{ borderRadius: "20px" }}
                      >
                        Add New Location
                      </Button>
                    </Box>
                    
                    {/* Search Field for Locations */}
                    <Box sx={{ p: 3, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Search locations by name..."
                        value={locationSearchQuery}
                        onChange={handleLocationSearch}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "#1976d2" }} />
                            </InputAdornment>
                          ),
                          endAdornment: locationSearchQuery && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={clearLocationSearch}
                                sx={{ color: "#1976d2" }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                      {locationSearchQuery && (
                        <Typography variant="body2" sx={{ mt: 1, color: "#1976d2" }}>
                          Showing {filteredLocations.length} of {locations.length} locations
                        </Typography>
                      )}
                    </Box>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              Location Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              Customer #
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              Address
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              City
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              County
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              State
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }} align="center">
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredLocations.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                                {locationSearchQuery ? (
                                  <>
                                    <SearchIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: "#1976d2" }} />
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                      No locations found
                                    </Typography>
                                    <Typography variant="body2">
                                      Try adjusting your search terms or{" "}
                                      <Button 
                                        onClick={clearLocationSearch}
                                        sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
                                      >
                                        clear search
                                      </Button>
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <LocationOnIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: "#1976d2" }} />
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                      No locations added yet
                                    </Typography>
                                    <Typography variant="body2">
                                      Click "Add New Location" to get started
                                    </Typography>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedLocations.map((location, index) => (
                              <TableRow 
                                key={index}
                                hover
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "#f8f9fa"
                                  },
                                  borderBottom: "1px solid #e0e0e0"
                                }}
                              >
                                <TableCell sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                                  {location.name}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  <Chip 
                                    label={location.customer_number || "N/A"} 
                                    size="small" 
                                    variant="outlined"
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  {location.address || "N/A"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  {location.city || "N/A"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  {location.county || "N/A"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  {location.state || "N/A"}
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                    <Tooltip title="Edit Location">
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() =>
                                          navigate(
                                            `/lawfirmlocationdetails/${location.law_firm_location_id}`
                                          )
                                        }
                                        sx={{
                                          "&:hover": {
                                            backgroundColor: "#1976d2",
                                            color: "white"
                                          }
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Location">
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() => handleDeleteLocationClick(location)}
                                        sx={{
                                          "&:hover": {
                                            backgroundColor: "error.main",
                                            color: "white"
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {filteredLocations.length > 0 && (
                      <TablePagination
          ActionsComponent={TablePaginationActions}
                        component="div"
                        count={filteredLocations.length}
                        page={locationsPage}
                        onPageChange={handleLocationsPageChange}
                        rowsPerPage={locationsRowsPerPage}
                        onRowsPerPageChange={handleLocationsRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderTop: "1px solid #e0e0e0",
                          "& .MuiTablePagination-toolbar": {
                            color: "#1976d2"
                          },
                          "& .MuiTablePagination-selectIcon": {
                            color: "#1976d2"
                          }
                        }}
                      />
                    )}
                  </Box>
                )}

                {/* Law Firm Users Tab */}
                {tabValue === 1 && (
                  <Box>
                    <Box sx={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      alignItems: "center",
                      p: 3,
                      backgroundColor: "#e3f2fd" // Light blue background
                    }}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: "600", 
                        color: "#1976d2", // Blue text
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                      }}>
                        <PeopleIcon />
                        Manage Law Firm Users
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                          onClick={handleOpenReassignLogs}
                          variant="outlined"
                          color="primary"
                          startIcon={<SwapHorizIcon />}
                          sx={{ borderRadius: "20px", whiteSpace: "nowrap" }}
                        >
                          View Re-Assignments
                        </Button>
                        <Button
                          onClick={() => navigate(`/addlawfirmuser/${id}`)}
                          variant="contained"
                          color="primary" // Changed to primary (blue) for consistency
                          startIcon={<AddIcon />}
                          sx={{ borderRadius: "20px", whiteSpace: "nowrap" }}
                        >
                          Add New User
                        </Button>
                      </Box>
                    </Box>

                    {/* Search Field for Users */}
                    <Box sx={{ p: 3, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Search users by name..."
                        value={userSearchQuery}
                        onChange={handleUserSearch}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "#1976d2" }} />
                            </InputAdornment>
                          ),
                          endAdornment: userSearchQuery && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={clearUserSearch}
                                sx={{ color: "#1976d2" }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                      {userSearchQuery && (
                        <Typography variant="body2" sx={{ mt: 1, color: "#1976d2" }}>
                          Showing {filteredTeamMembers.length} of {teamMembers.length} users
                        </Typography>
                      )}
                    </Box>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              User Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              Email
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              Location
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                              Position
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }} align="center">
                              Admin
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }} align="center">
                              Active
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }} align="center">
                              Default Attorney
                            </TableCell>
                            <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }} align="center">
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredTeamMembers.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.secondary" }}>
                                {userSearchQuery ? (
                                  <>
                                    <SearchIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: "#1976d2" }} />
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                      No users found
                                    </Typography>
                                    <Typography variant="body2">
                                      Try adjusting your search terms or{" "}
                                      <Button 
                                        onClick={clearUserSearch}
                                        sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
                                      >
                                        clear search
                                      </Button>
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <PeopleIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: "#1976d2" }} />
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                      No users added yet
                                    </Typography>
                                    <Typography variant="body2">
                                      Click "Add New User" to get started
                                    </Typography>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedTeamMembers.map((member, index) => (
                              <TableRow 
                                key={index}
                                hover
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "#f8f9fa"
                                  },
                                  borderBottom: "1px solid #e0e0e0"
                                }}
                              >
                                <TableCell sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {member.is_admin && (
                                      <AdminPanelSettingsIcon 
                                        color="primary" 
                                        fontSize="small"
                                      />
                                    )}
                                    {member.lawfirm_user_name}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  {member.firm_email ? (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                      <Box component="span">
                                        {member.firm_email}
                                        <Typography
                                          component="span"
                                          sx={{
                                            fontSize: "0.75rem",
                                            color: "primary.main",
                                            fontWeight: 600,
                                            ml: 0.5,
                                          }}
                                        >
                                          (Firm)
                                        </Typography>
                                      </Box>
                                      {member.email && member.email !== member.firm_email && (
                                        <Tooltip title="Primary email" arrow placement="top">
                                          <Typography
                                            variant="caption"
                                            sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}
                                          >
                                            {member.email}
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  ) : (
                                    member.email || "N/A"
                                  )}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  {member.location || "N/A"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  <Chip 
                                    label={member.Position || "N/A"} 
                                    size="small" 
                                    variant="outlined"
                                    color="primary" // Changed to primary (blue) for consistency
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {member.is_admin ? (
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  ) : (
                                    <CancelIcon color="disabled" fontSize="small" />
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Switch
                                    checked={member.is_active}
                                    onChange={() =>
                                      handleToggle(
                                        teamMembersPage * teamMembersRowsPerPage + index,
                                        "is_active",
                                        setTeamMembers,
                                        teamMembers
                                      )
                                    }
                                    size="small"
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Switch
                                    checked={member.is_default}
                                    onChange={() =>
                                      handleToggle(
                                        teamMembersPage * teamMembersRowsPerPage + index,
                                        "is_default",
                                        setTeamMembers,
                                        teamMembers
                                      )
                                    }
                                    size="small"
                                    color="primary" // Changed to primary (blue) for consistency
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                    {(role === "super_user" || role === "internal_user") && (
                                      <Tooltip title="Re-assign Policies">
                                        <IconButton
                                          color="success"
                                          size="small"
                                          onClick={() => openReassignDialog(member)}
                                          sx={{
                                            "&:hover": {
                                              backgroundColor: "success.main",
                                              color: "white"
                                            }
                                          }}
                                        >
                                          <SwapHorizIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Edit User">
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() =>
                                          navigate(
                                            `/editlawfirmuser/${member.lawfirm_user_id}/${member.location_id}`
                                          )
                                        }
                                        sx={{
                                          "&:hover": {
                                            backgroundColor: "#1976d2",
                                            color: "white"
                                          }
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete User">
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() => handleDeleteUserClick(member)}
                                        sx={{
                                          "&:hover": {
                                            backgroundColor: "error.main",
                                            color: "white"
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {filteredTeamMembers.length > 0 && (
                      <TablePagination
          ActionsComponent={TablePaginationActions}
                        component="div"
                        count={filteredTeamMembers.length}
                        page={teamMembersPage}
                        onPageChange={handleTeamMembersPageChange}
                        rowsPerPage={teamMembersRowsPerPage}
                        onRowsPerPageChange={handleTeamMembersRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderTop: "1px solid #e0e0e0",
                          "& .MuiTablePagination-toolbar": {
                            color: "#1976d2"
                          },
                          "& .MuiTablePagination-selectIcon": {
                            color: "#1976d2"
                          }
                        }}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Delete Location Confirmation Dialog */}
      <Dialog
        open={deleteLocationDialogOpen}
        onClose={() => setDeleteLocationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "error.light", color: "white" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon />
            Confirm Deletion
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>
              {selectedLocation?.name ? selectedLocation.name : "this location"}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setDeleteLocationDialogOpen(false)}
            variant="outlined"
            disabled={deleteLocationLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteLocation}
            color="error"
            variant="contained"
            disabled={deleteLocationLoading}
          >
            {deleteLocationLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Law Firm User Confirmation Dialog */}
      <Dialog
        open={deleteUserDialogOpen}
        onClose={() => setDeleteUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "error.light", color: "white" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon />
            Confirm Deletion
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>
              {selectedUser?.lawfirm_user_name
                ? selectedUser.lawfirm_user_name
                : "this user"}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setDeleteUserDialogOpen(false)}
            variant="outlined"
            disabled={deleteUserLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteUser}
            color="error"
            variant="contained"
            disabled={deleteUserLoading}
          >
            {deleteUserLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-assign Policies Dialog */}
      <Dialog
        open={reassignDialogOpen}
        onClose={() => setReassignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SwapHorizIcon />
            Re-assign Policies
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Position: <strong>{reassignFromMember?.Position || "N/A"}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              From: <strong>{reassignFromMember?.lawfirm_user_name || "N/A"}</strong>
            </Typography>
            <Autocomplete
              options={
                (replacementUsers || []).map((u) => ({
                  label: u.lawfirm_user_name,
                  value: u.lawfirm_user_id,
                })) }
              value={selectedReplacementUser}
              onChange={(e, v) => setSelectedReplacementUser(v)}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Select replacement user" />
              )}
            />

            <Autocomplete
              multiple
              options={[
                ...(availableBonds && availableBonds.length
                  ? [{ label: "All Bonds", value: "ALL" }]
                  : []),
                ...(availableBonds || []).map((bond) => ({
                  label: `${bond.bond_id} - ${bond.bond_type} - ${
                    bond.name || "N/A"
                  } (${bond.status})`,
                  value: bond.bond_id,
                })),
              ]}
              value={selectedBondOptions}
              onChange={(e, v) => {
                const includesAll = v.some((opt) => opt.value === "ALL");
                if (includesAll) {
                  setSelectedBondOptions(
                    [{ label: "All Bonds", value: "ALL" }]
                  );
                } else {
                  setSelectedBondOptions(v.filter((opt) => opt.value !== "ALL"));
                }
              }}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Select bond numbers" />
              )}
            />
            {bondsLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Loading bonds for this user...
                </Typography>
              </Box>
            )}
            {bondsError && !bondsLoading && (
              <Typography variant="body2" sx={{ color: "error.main" }}>
                {bondsError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setReassignDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReassign}
            color="primary"
            variant="contained"
            disabled={
              !selectedReplacementUser ||
              selectedBondOptions.length === 0 ||
              reassignSubmitting ||
              bondsLoading
            }
          >
            {reassignSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Law Firm Re-assignment Logs Dialog */}
      <Dialog
        open={reassignLogsDialogOpen}
        onClose={() => setReassignLogsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SwapHorizIcon />
            Law Firm Re-Assignments
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            mt: 1,
            maxHeight: 480,
            overflowY: "auto",
            backgroundColor: "#f8f9fa",
          }}
        >
          {reassignLogsLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!reassignLogsLoading && reassignLogsError && (
            <Typography
              variant="body2"
              sx={{ color: "error.main", textAlign: "center", py: 2 }}
            >
              {reassignLogsError}
            </Typography>
          )}

          {!reassignLogsLoading &&
            !reassignLogsError &&
            (!reassignLogs || reassignLogs.length === 0) && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
              >
                No reassignment activity found for this law firm.
              </Typography>
            )}

          {!reassignLogsLoading &&
            Array.isArray(reassignLogs) &&
            reassignLogs.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {reassignLogs.map((log) => (
                  <Paper
                    key={log.reassignment_log_id}
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: "white",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, display: "flex", gap: 1 }}
                      >
                        <span>{log.lawfirm_name || "Law Firm"}</span>
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {log.role && (
                          <Chip
                            label={log.role}
                            size="small"
                            color={
                              log.role === "Attorney" ? "primary" : "secondary"
                            }
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {formatDateTime(log.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>From:</strong>{" "}
                      {log.lawfirm_user_from || "Unassigned"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>To:</strong> {log.lawfirm_user_to || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Comment:</strong> {renderReassignComment(log.comment)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      <strong>Updated By:</strong>{" "}
                      {log.created_by || "Unknown"}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setReassignLogsDialogOpen(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LawFirmDetail;
